/* global axios */
import ApiClient from './ApiClient';

const getTimeOffset = () => -new Date().getTimezoneOffset() / 60;

class ReportsAPI extends ApiClient {
  constructor() {
    super('reports', { accountScoped: true, apiVersion: 'v2' });
  }

  getReports({
    metric,
    from,
    to,
    type = 'account',
    id,
    groupBy,
    businessHours,
    team_id = 0,
  }) {
    return axios.get(`${this.url}`, {
      params: {
        metric,
        since: from,
        until: to,
        type,
        id,
        group_by: groupBy,
        business_hours: businessHours,
        timezone_offset: getTimeOffset(),
        team_id,
      },
    });
  }

  // eslint-disable-next-line default-param-last
  getSummary(since, until, type = 'account', id, groupBy, businessHours) {
    return axios.get(`${this.url}/summary`, {
      params: {
        since,
        until,
        type,
        id,
        group_by: groupBy,
        business_hours: businessHours,
        timezone_offset: getTimeOffset(),
      },
    });
  }

  getConversationMetric(type = 'account', page = 1, team_id = 0) {
    return axios.get(`${this.url}/conversations`, {
      params: {
        type,
        page,
        team_id,
      },
    });
  }

  getAgentReports({ from: since, to: until, businessHours }) {
    return axios.get(`${this.url}/agents`, {
      params: { since, until, business_hours: businessHours },
    });
  }

  getConversationTrafficCSV() {
    return axios.get(`${this.url}/conversation_traffic`, {
      params: { timezone_offset: getTimeOffset() },
    });
  }

  getLabelReports({ from: since, to: until, businessHours }) {
    return axios.get(`${this.url}/labels`, {
      params: { since, until, business_hours: businessHours },
    });
  }

  getInboxReports({ from: since, to: until, businessHours }) {
    return axios.get(`${this.url}/inboxes`, {
      params: { since, until, business_hours: businessHours },
    });
  }

  getTeamReports({ from: since, to: until, businessHours }) {
    return axios.get(`${this.url}/teams`, {
      params: { since, until, business_hours: businessHours },
    });
  }

  getBotMetrics({ from, to } = {}) {
    return axios.get(`${this.url}/bot_metrics`, {
      params: { since: from, until: to },
    });
  }

  getBotSummary({ from, to, groupBy, businessHours } = {}) {
    return axios.get(`${this.url}/bot_summary`, {
      params: {
        since: from,
        until: to,
        type: 'account',
        group_by: groupBy,
        business_hours: businessHours,
      },
    });
  }

  getInboundDashboard({
    from,
    to,
    team_id,
    inbox_id,
    agent_ids = [],
    channel_types = [],
    statuses = [],
    bot_active,
    stuck_threshold_minutes,
  } = {}) {
    return axios.get(`${this.url}/inbound`, {
      params: {
        since: from,
        until: to,
        timezone_offset: getTimeOffset(),
        team_id,
        inbox_id,
        agent_ids,
        channel_types,
        statuses,
        bot_active,
        stuck_threshold_minutes,
      },
    });
  }

  getInboundDrilldown({
    from,
    to,
    metric,
    entity_id,
    page = 1,
    ...filters
  } = {}) {
    return axios.get(`${this.url}/inbound_drilldown`, {
      params: {
        since: from,
        until: to,
        timezone_offset: getTimeOffset(),
        metric,
        entity_id,
        page,
        ...filters,
      },
    });
  }

  getOutboundDashboard({
    from,
    to,
    team_id,
    agent_ids = [],
    inbox_id,
    label_ids = [],
  } = {}) {
    return axios.get(`${this.url}/outbound`, {
      params: {
        since: from,
        until: to,
        timezone_offset: getTimeOffset(),
        team_id,
        agent_ids,
        inbox_id,
        label_ids,
      },
    });
  }

  getOutboundDrilldown({ from, to, metric, page = 1, ...filters } = {}) {
    return axios.get(`${this.url}/outbound_drilldown`, {
      params: {
        since: from,
        until: to,
        timezone_offset: getTimeOffset(),
        metric,
        page,
        ...filters,
      },
    });
  }

  getOutboundExport({ from, to, ...filters } = {}) {
    return axios.get(`${this.url}/outbound_export`, {
      params: {
        since: from,
        until: to,
        ...filters,
      },
    });
  }

  getInboundExport({ from, to, ...filters } = {}) {
    return axios.get(`${this.url}/inbound_export`, {
      params: {
        since: from,
        until: to,
        ...filters,
      },
    });
  }

  getOutboundMetaInsights({ inbox_id } = {}) {
    return axios.get(`${this.url}/outbound_meta_insights`, {
      params: {
        inbox_id,
      },
    });
  }

  getCampaignsDashboard({
    from,
    to,
    team_id,
    inbox_id,
    campaign_status = [],
    campaign_type = [],
  } = {}) {
    return axios.get(`${this.url}/campaigns`, {
      params: {
        since: from,
        until: to,
        timezone_offset: getTimeOffset(),
        team_id,
        inbox_id,
        campaign_status,
        campaign_type,
      },
    });
  }

  getCampaignsExport({ from, to, ...filters } = {}) {
    return axios.get(`${this.url}/campaigns_export`, {
      params: {
        since: from,
        until: to,
        ...filters,
      },
    });
  }

  getCampaignsDrilldown({ from, to, metric, page = 1, ...filters } = {}) {
    return axios.get(`${this.url}/campaigns_drilldown`, {
      params: {
        since: from,
        until: to,
        timezone_offset: getTimeOffset(),
        metric,
        page,
        ...filters,
      },
    });
  }

  getCampaignSummary({ from: since, to: until, ...params }) {
    const accountId = this.accountIdFromRoute;
    return axios.get(`/api/v1/accounts/${accountId}/campaign_reports/summary`, {
      params: { since, until, ...params },
    });
  }

  getTemplatePerformance({ from: since, to: until, ...params }) {
    const accountId = this.accountIdFromRoute;
    return axios.get(
      `/api/v1/accounts/${accountId}/campaign_reports/template_performance`,
      {
        params: { since, until, ...params },
      }
    );
  }

  getCampaignContactList({ from: since, to: until, ...params }) {
    const accountId = this.accountIdFromRoute;
    return axios.get(
      `/api/v1/accounts/${accountId}/campaign_reports/contact_list`,
      {
        params: { since, until, ...params },
      }
    );
  }

  async exportCampaignCSV({ from: since, to: until, fileName, ...params }) {
    const accountId = this.accountIdFromRoute;
    const response = await axios.get(
      `/api/v1/accounts/${accountId}/campaign_reports/export_csv`,
      {
        params: { since, until, ...params },
        responseType: 'blob',
      }
    );
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${fileName}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  getCampaignsMetaInsights({ inbox_id } = {}) {
    return axios.get(`${this.url}/campaigns_meta_insights`, {
      params: {
        inbox_id,
      },
    });
  }

  getUraNavigation({ chatId } = {}) {
    return axios.get(`${this.url}/ura_navigation`, {
      params: {
        chat_id: chatId,
      },
    });
  }

  getUraNavigationConversations({ chatId, since, until, page } = {}) {
    return axios.get(`${this.url}/ura_navigation_conversations`, {
      params: {
        chat_id: chatId,
        since,
        until,
        page,
      },
    });
  }

  getUraNavigationConversationsExportCSV({ chatId, since, until } = {}) {
    return axios.get(`${this.url}/ura_navigation_conversations_export`, {
      params: {
        chat_id: chatId,
        since,
        until,
      },
    });
  }
}

export default new ReportsAPI();
